import { useSelector, useDispatch } from "react-redux";
import { useFetchRolesQuery } from "../../Apis/rolesApi";
import { RolesList } from "../../Model/userObjects";
import { enterpriseLevelsDetailsSelector } from "../../Reducers/enterpriseLevelsDetailsReducer";
import { usersUpdateSelector } from "../../Reducers/usersUpdateReducer";
import { useMemo, useCallback, useEffect } from "react";
import { ENTERPRISE, REGIONAL, SUB_REGIONAL } from "../../Enums/administratorLevels";
import CustomSelect from "../CommonComponents/select/CustomSelect";
import CrossIcon from "../Icons/cross-icon";
import styles from "./users-and-roles.module.css";
import { EDIT_MODAL_ACTIONS, EDIT_USER_ACTIONS } from "./UserEditModal";
import { updateUser } from "../../Actions/userActions";

const administratorLevelOptions = [
  { value: ENTERPRISE, label: "Enterprise" },
  { value: REGIONAL, label: "Region" },
  { value: SUB_REGIONAL, label: "Subregion" },
]

export const UserEditForm = ({ onClose, user, userDispatch, modalDispatch }) => {
  const dispatch = useDispatch();
  const { data: roles = new RolesList() } = useFetchRolesQuery();
  const enterpriseLevelsDetails = useSelector(enterpriseLevelsDetailsSelector);
  const usersUpdate = useSelector(usersUpdateSelector);

  const roleOptions = useMemo(() => {
    return roles.getRoles().map(role => ({
      value: role.getId(),
      label: role.getName(),
    }));
  }, [roles]);

  const administratorLevelDisplayFunc = useCallback(() => {
    if (enterpriseLevelsDetails === null) return "";
    const level = user.administratorLevel;
    const id = user.accessId;
    if (!level || !id) return "";
    return enterpriseLevelsDetails.getInfo(level, id).name;
  }, [enterpriseLevelsDetails, user]);

  const selectAdministratorLevel = useCallback((value) => {
    let updatedUser;
    switch (value) {
      case ENTERPRISE:
        updatedUser = { ...user, accessId: enterpriseLevelsDetails.getEnterprise().getId(), administratorLevel: ENTERPRISE };
        userDispatch({ 
          type: EDIT_USER_ACTIONS.UPDATE_USER, 
          payload: updatedUser 
        });
        break;
      case REGIONAL:
        modalDispatch({ type: EDIT_MODAL_ACTIONS.SELECT_REGION });
        break;
      case SUB_REGIONAL:
        modalDispatch({ type: EDIT_MODAL_ACTIONS.SELECT_SUBREGION });
        break;
      default:
        return;
    }
  }, [userDispatch, user, enterpriseLevelsDetails, modalDispatch]);

  const disabled = !user.roleId || !user.accessId || !user.administratorLevel || usersUpdate.loading;

  useEffect(() => {
    if (usersUpdate.success) {
      onClose();
    }
  }, [usersUpdate.success, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (disabled) return;

    const updateData = {
      accessLevel: user.accessLevel,
      administratorLevel: user.administratorLevel, 
      accessId: user.accessId,
      isActive: user.isActive,
      roleId: user.roleId,
    };
    
    dispatch(updateUser(user.id, updateData));
  }

  return (
    <div className={styles.editFormContainer}>
      <div className={styles.editFormHeader}>
        <div className={styles.editFormHeaderContent}>
          <div className={styles.editFormTitle}>Edit user</div>
        </div>
        <button className={styles.editFormCloseButton} type="button" onClick={onClose}>
          <CrossIcon />
        </button>
      </div>

      <form className={styles.editFormContent} onSubmit={handleSubmit}>
        <div className={styles.editFormDescription}>
          Update this user's role and access level permissions.
        </div>

        <div className={styles.editFormFields}>
          <div className={styles.editFormField}>
            <div className={styles.editFormFieldInput}>
              {user.name}
            </div>
          </div>
          
          <div className={styles.editFormField}>
            <div className={styles.editFormFieldInput}>
              {user.email}
            </div>
          </div>
          
          <div className={styles.editFormFieldRow}>
            <div className={styles.editFormSelectWrapper}>
              <div className={styles.editFormSelectLabel}>
                <div className={styles.editFormSelectLabelText}>Select a role</div>
              </div>
              <CustomSelect
                options={roleOptions}
                selected={user.roleId}
                onSelect={(value) => {
                  userDispatch({ 
                    type: EDIT_USER_ACTIONS.UPDATE_USER, 
                    payload: { ...user, roleId: value }
                  });
                }}
                selectClassName={styles.editFormCustomSelect}
                wrapperClassName={styles.editFormCustomSelectWrapper}
              />
            </div>
            
            <div className={styles.editFormSelectWrapper}>
              <div className={styles.editFormSelectLabel}>
                <div className={styles.editFormSelectLabelText}>Select access level</div>
              </div>
              <CustomSelect
                options={administratorLevelOptions}
                selected={user.administratorLevel}
                onSelect={selectAdministratorLevel}
                selectClassName={styles.editFormCustomSelect}
                wrapperClassName={styles.editFormCustomSelectWrapper}
                displayFunc={administratorLevelDisplayFunc}
              />
            </div>
          </div>
        </div>
      </form>

      <div className={styles.editFormFooter}>
        <button className={styles.editFormCancelButton} type="button" onClick={onClose}>
          Cancel
        </button>
        <button 
          className={styles.editFormSubmitButton} 
          type="submit" 
          disabled={disabled}
          onClick={handleSubmit}
        >
          {usersUpdate.loading ? 'Updating user...' : 'Update user'}
        </button>
      </div>
    </div>
  );
};
